import React, { useState, useEffect } from "react";
import { Box, TextField, InputAdornment, Typography } from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { FieldProps } from "@/core/components/Field";
import { useTranslations as t } from "@/core/hooks/useTranslations/useTranslations";
import { getBasicConfig } from "@/core/utils";
import { useFieldError } from "@/core/hooks/useFieldError/useFieldError";
import { useDefaultValue } from "@/core/hooks/useDefaultValue/useDefaultValue";

const MAX_CURRENCY_INPUT_VALUE = 10000;
const DEFAULT_CURRENCY = "Euro";

const CurrencyInput = ({ field }: FieldProps) => {
  const { properties, key, defaultValue } = field;
  const {
    clearErrors,
    formState: { errors },
    control,
    watch,
    setValue,
    trigger,
  } = useFormContext();

  const [amount, setAmount] = useState<string>("");
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [inputDefaultValue, setInputDefaultValue] = useState<number>();
  const formValue = watch(key);

  const { hasError, errorMessage, errorTranslationKey } = useFieldError(
    errors,
    field
  );
  const defaulInputValue = useDefaultValue(defaultValue);

  const locale = getBasicConfig({})?.language || "en-US";
  const decimal = (1.1).toLocaleString(locale).substring(1, 2);
  const maxVal = Number(field.validate?.max) || MAX_CURRENCY_INPUT_VALUE;

  const formatValue = (val: number) =>
    new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(val);

  useEffect(() => {
    if (defaulInputValue && (formValue == null || formValue === "")) {
      setInputDefaultValue(defaulInputValue);
      setValue(key, defaulInputValue);
      if (Number(defaulInputValue) !== 0) {
        setAmount(formatValue(defaulInputValue));
      } else {
        setAmount("");
      }
    }
  }, [defaulInputValue]);

  useEffect(() => {
    if (!isFocused) {
      if (!formValue && formValue !== 0) {
        setAmount("");
      } else if (Number(formValue) === 0) {
        setAmount("");
      } else {
        setAmount(formatValue(Number(formValue)));
      }
    }
  }, [formValue, isFocused]);

  const handleFocus = () => {
    setIsFocused(true);
    if (amount) {
      const lastDecimalIndex = Math.max(
        amount.lastIndexOf("."),
        amount.lastIndexOf(",")
      );

      if (lastDecimalIndex !== -1) {
        const numbers = amount.replace(/[^\d]/g, "");
        const result = numbers.slice(0, -2) + decimal + numbers.slice(-2);
        setAmount(result);
      } else {
        // No decimal separator - just keep numbers
        setAmount(amount.replace(/[^\d]/g, ""));
      }
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    clearErrors(key);
    let input = e.target.value.replace(/[^0-9.,]/g, "");
    input = input.replace(/[.,]/g, decimal);
    setAmount(input);

    if (!input || input === decimal) {
      setValue(key, "");
      trigger(key);
      return;
    }

    const decimalCount = input.split(decimal).length - 1;
    if (decimalCount > 1) {
      const parts = input.split(decimal);
      input = parts[0] + decimal + parts.slice(1).join("");
    }
    setAmount(input);

    if (input.includes(decimal)) {
      const [whole, fraction] = input.split(decimal);
      if (fraction && fraction.length > 2) {
        input = whole + decimal + fraction.slice(0, 2);
        setAmount(input);
      }
    }
    const parsed = parseFloat(input.replace(decimal, "."));
    if (!isNaN(parsed)) {
      setValue(key, parsed);
      trigger(key);
    }
  };

  const handleBlur = () => {
    setIsFocused(false);
    if (!amount) {
      setValue(key, "");
      setAmount("");
      return;
    }

    const parsed = parseFloat(amount.replace(decimal, "."));
    if (isNaN(parsed) || parsed === 0) {
      setValue(key, "");
      setAmount("");
      return;
    }

    if (parsed > maxVal) {
      setValue(key, maxVal);
      setAmount(formatValue(maxVal));
      return;
    }

    setValue(key, parsed);
    setAmount(formatValue(parsed));
  };

  return (
    <Controller
      name={key}
      control={control}
      rules={{
        required: {
          value: field.validate?.required || false,
          message: t(`validate.${errorTranslationKey}.required.message`),
        },
      }}
      defaultValue={inputDefaultValue || ""}
      render={() => (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <TextField
            error={hasError}
            helperText={hasError ? errorMessage : ""}
            label={t("compensationReceivedForm.amount")}
            variant="outlined"
            fullWidth
            onFocus={handleFocus}
            onBlur={handleBlur}
            type="text"
            value={amount}
            onChange={handleChange}
            inputProps={{
              "data-testid": "currency-input-field",
              "aria-label": t("compensationReceivedForm.amount"),
            }}
            InputProps={{
              endAdornment: (amount !== "" || isFocused) && (
                <InputAdornment position="end">
                  <Typography sx={{ color: "rgba(0, 0, 0, 0.6)" }}>
                    {properties?.unitLabel || DEFAULT_CURRENCY}
                  </Typography>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}
    />
  );
};

export default CurrencyInput;
